/**
 * EventsInputStep — combines the input-method toggle, single-entry form, CSV
 * upload, and the post-parse Flouri validation review into one screen.
 *
 * Single-entry: a small form for one row (hashes PII before adding to events).
 * CSV: drag-or-pick a file, parser hashes PII and produces an event list +
 * per-row rejections; we render rejections inline so the user can fix the
 * file and re-upload.
 *
 * On submit, the parent advances to ConsentStep + UploadStep with the
 * cleaned `events[]`.
 */
import React, { useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import type { OfflineConversionEvent } from '@/services/api/offlineConversions';
import { buildCsvTemplate, MAX_ROWS_PER_FILE, parseCsv } from '../utils/csvParser';
import { hashEmail, hashName, hashPhone } from '../utils/hashing';
import { deriveTransactionId, normalizeCurrency, parseConversionValue, toRfc3339Utc } from '../utils/normalizers';
import type { InputMethod } from '../types';

interface Props {
  conversionActionId: string;
  inputMethod: InputMethod;
  events: OfflineConversionEvent[];
  parseRejections: Array<{ rowIndex: number; field?: string; reason: string }>;
  onInputMethodChange: (m: InputMethod) => void;
  onEventsChange: (events: OfflineConversionEvent[]) => void;
  onRejectionsChange: (
    rejections: Array<{ rowIndex: number; field?: string; reason: string }>
  ) => void;
}

interface SingleEntryFormState {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  postalCode: string;
  eventTimestamp: string;
  conversionValue: string;
  currency: string;
  transactionId: string;
  gclid: string;
}

const emptySingleEntry: SingleEntryFormState = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  countryCode: '',
  postalCode: '',
  eventTimestamp: '',
  conversionValue: '',
  currency: '',
  transactionId: '',
  gclid: '',
};

export const EventsInputStep: React.FC<Props> = ({
  conversionActionId,
  inputMethod,
  events,
  parseRejections,
  onInputMethodChange,
  onEventsChange,
  onRejectionsChange,
}) => {
  const [single, setSingle] = useState<SingleEntryFormState>(emptySingleEntry);
  const [singleError, setSingleError] = useState<string | null>(null);
  const [singleBusy, setSingleBusy] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const downloadTemplate = () => {
    const blob = new Blob([buildCsvTemplate()], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'offline-conversions-template.csv';
    a.click();
    // Defer the revoke — some browsers (notably older Safari) can cancel or
    // truncate the download if the blob URL is revoked synchronously after
    // `click()`, because the navigation hasn't actually started yet. A short
    // timeout lets the browser commit to the download first. Using a
    // generous 1s ensures the URL stays valid even on slow devices.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleSingleAdd = async () => {
    setSingleError(null);
    setSingleBusy(true);
    try {
      const rfc3339 = toRfc3339Utc(single.eventTimestamp);
      if (!rfc3339) {
        throw new Error('Event timestamp is required and must be a valid date.');
      }
      const ev: OfflineConversionEvent = { event_timestamp: rfc3339 };

      if (single.email) ev.hashed_email = await hashEmail(single.email);
      if (single.phone) ev.hashed_phone = await hashPhone(single.phone);
      if (single.firstName || single.lastName || single.countryCode || single.postalCode) {
        if (!(single.firstName && single.lastName && single.countryCode && single.postalCode)) {
          throw new Error(
            'Address is all-or-nothing: provide first name, last name, country code, and postal code.'
          );
        }
        const cc = single.countryCode.trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(cc)) {
          throw new Error('Country code must be ISO-3166-1 alpha-2 (two letters).');
        }
        ev.hashed_address = {
          hashed_first_name: await hashName(single.firstName),
          hashed_last_name: await hashName(single.lastName),
          country_code: cc,
          postal_code: single.postalCode.trim(),
        };
      }

      if (single.gclid) ev.gclid = single.gclid;

      if (
        !ev.hashed_email &&
        !ev.hashed_phone &&
        !ev.hashed_address &&
        !ev.gclid
      ) {
        throw new Error('Provide at least one identifier (email, phone, address, or gclid).');
      }

      if (single.conversionValue) {
        const v = parseConversionValue(single.conversionValue);
        if (v === null) throw new Error('Conversion value must be a non-negative number.');
        ev.conversion_value = v;
        const cur = normalizeCurrency(single.currency);
        if (!cur) throw new Error('Currency is required when conversion value is set (e.g. USD).');
        ev.currency = cur;
      }

      ev.transaction_id =
        single.transactionId ||
        (await deriveTransactionId(
          { email: single.email || null, phone: single.phone || null, event_timestamp: rfc3339 },
          conversionActionId
        ));

      onEventsChange([...events, ev]);
      setSingle(emptySingleEntry);
    } catch (err) {
      setSingleError((err as Error).message);
    } finally {
      setSingleBusy(false);
    }
  };

  const handleFile = async (file: File) => {
    setCsvError(null);
    setCsvBusy(true);
    try {
      const text = await file.text();
      const result = await parseCsv({ csvText: text, conversionActionId });
      onEventsChange(result.events);
      onRejectionsChange(
        result.rejections.map((r) => ({
          rowIndex: r.rowIndex,
          field: r.field,
          reason: r.reason,
        }))
      );
    } catch (err) {
      setCsvError((err as Error).message);
    } finally {
      setCsvBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Input-method switcher. Generic `role="group"` rather than
          `radiogroup` because the children are toggle BUTTONS
          (`aria-pressed`), not radio inputs — `radiogroup` would mislead
          screen readers into expecting `role="radio"` children, arrow-key
          navigation, and `aria-checked`. The button-with-aria-pressed
          pattern doesn't have those obligations; `group` + an `aria-label`
          for the group name is the right semantic. */}
      <div
        className="flex gap-2"
        role="group"
        aria-label="Input method"
      >
        <Button
          variant={inputMethod === 'csv' ? 'primary' : 'secondary'}
          aria-pressed={inputMethod === 'csv'}
          onClick={() => onInputMethodChange('csv')}
        >
          CSV upload
        </Button>
        <Button
          variant={inputMethod === 'single' ? 'primary' : 'secondary'}
          aria-pressed={inputMethod === 'single'}
          onClick={() => onInputMethodChange('single')}
        >
          Single entry
        </Button>
      </div>

      {inputMethod === 'csv' && (
        <Card className="space-y-3">
          <p className="text-sm text-gray-600">
            Up to {MAX_ROWS_PER_FILE} rows per file. Email/phone/name are
            hashed in your browser before sending; plaintext customer data
            never leaves your device.
          </p>
          <Button variant="ghost" onClick={downloadTemplate}>
            Download CSV template
          </Button>
          <Input
            type="file"
            accept=".csv,text/csv"
            disabled={csvBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          {csvBusy && <p className="text-sm text-gray-500">Parsing…</p>}
          {csvError && <Alert variant="error">{csvError}</Alert>}
          {events.length > 0 && (
            <p className="text-sm text-gray-700">
              Parsed <strong>{events.length}</strong> row{events.length === 1 ? '' : 's'} ready to upload.
            </p>
          )}
          {parseRejections.length > 0 && (
            <Alert variant="warning">
              <p className="font-medium">
                {parseRejections.length} row{parseRejections.length === 1 ? '' : 's'} skipped
              </p>
              <ul className="ml-4 mt-1 list-disc text-sm">
                {parseRejections.slice(0, 5).map((r) => (
                  <li key={r.rowIndex}>
                    Row {r.rowIndex + 2}: {r.reason}
                    {r.field ? ` (column: ${r.field})` : ''}
                  </li>
                ))}
                {parseRejections.length > 5 && (
                  <li>…{parseRejections.length - 5} more.</li>
                )}
              </ul>
            </Alert>
          )}
        </Card>
      )}

      {inputMethod === 'single' && (
        <Card className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              placeholder="Email"
              value={single.email}
              onChange={(e) => setSingle({ ...single, email: e.target.value })}
            />
            <Input
              placeholder="Phone (+E.164)"
              value={single.phone}
              onChange={(e) => setSingle({ ...single, phone: e.target.value })}
            />
            <Input
              placeholder="First name"
              value={single.firstName}
              onChange={(e) => setSingle({ ...single, firstName: e.target.value })}
            />
            <Input
              placeholder="Last name"
              value={single.lastName}
              onChange={(e) => setSingle({ ...single, lastName: e.target.value })}
            />
            <Input
              placeholder="Country code (e.g. US)"
              value={single.countryCode}
              onChange={(e) => setSingle({ ...single, countryCode: e.target.value })}
            />
            <Input
              placeholder="Postal code"
              value={single.postalCode}
              onChange={(e) => setSingle({ ...single, postalCode: e.target.value })}
            />
            <Input
              type="datetime-local"
              placeholder="Event timestamp"
              value={single.eventTimestamp}
              onChange={(e) => setSingle({ ...single, eventTimestamp: e.target.value })}
            />
            <Input
              placeholder="GCLID (optional)"
              value={single.gclid}
              onChange={(e) => setSingle({ ...single, gclid: e.target.value })}
            />
            <Input
              placeholder="Conversion value"
              value={single.conversionValue}
              onChange={(e) =>
                setSingle({ ...single, conversionValue: e.target.value })
              }
            />
            <Input
              placeholder="Currency (e.g. USD)"
              value={single.currency}
              onChange={(e) => setSingle({ ...single, currency: e.target.value })}
            />
            <Input
              placeholder="Transaction ID (auto if blank)"
              value={single.transactionId}
              onChange={(e) =>
                setSingle({ ...single, transactionId: e.target.value })
              }
            />
          </div>
          {singleError && <Alert variant="error">{singleError}</Alert>}
          <div className="flex justify-end">
            <Button variant="primary" onClick={handleSingleAdd} disabled={singleBusy}>
              {singleBusy ? 'Hashing…' : 'Add to upload'}
            </Button>
          </div>

          {events.length > 0 && (
            <div>
              <p className="text-sm text-gray-700">
                <strong>{events.length}</strong> row{events.length === 1 ? '' : 's'} ready to upload.
              </p>
              <Button
                variant="ghost"
                onClick={() => onEventsChange(events.slice(0, -1))}
              >
                Undo last
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
